import { TFunction } from "i18next";
import { SecondaryColor, SemanticColor } from "node-library/dist/colors";
import {
	FrontendContentPage,
	UserStatus,
	SystemRole,
} from "node-library/dist/constants";
import { UserTableSortColumn } from "node-library/dist/paginations";
import {
	FrontendBatchType,
	FrontendCompanyTestCategory,
	FrontendUserInfo,
} from "node-library/dist/portal";

export function getUserStatusString(
	status: UserStatus,
	translation: TFunction<"translation", undefined>,
): string {
	switch (status) {
		case UserStatus.PENDING_APPROVAL:
			return translation("Pending");
		case UserStatus.VALIDATING_EMAIL:
			return translation("Validating");
		case UserStatus.REGISTERED:
			return translation("Registered");
		case UserStatus.REGISTRATION_REJECTED:
			return translation("Rejected");
	}
}

export function getUserSortString(
	sortColumn: UserTableSortColumn,
	translation: TFunction<"translation", undefined>,
): string {
	switch (sortColumn) {
		case UserTableSortColumn.INSERTED:
			return translation("RegistrationTime");

		case UserTableSortColumn.USER_NAME:
			return translation("Email");
		case UserTableSortColumn.ROLE:
			return translation("Role");
		case UserTableSortColumn.FIRST_NAME:
			return translation("FirstName");
		case UserTableSortColumn.LAST_NAME:
			return translation("LastName");
	}
}

export function getUserStatusColors(status: UserStatus): {
	backgroundColor: string;
	borderColor: string;
	textColor: string;
} {
	switch (status) {
		case UserStatus.PENDING_APPROVAL:
			return {
				backgroundColor: SemanticColor.WARNING_100,
				borderColor: SemanticColor.WARNING,
				textColor: SemanticColor.WARNING_300,
			};
		case UserStatus.VALIDATING_EMAIL:
			return {
				backgroundColor: SecondaryColor.SECONDARY_100,
				borderColor: SecondaryColor.SECONDARY_500,
				textColor: SecondaryColor.SECONDARY_600,
			};
		case UserStatus.REGISTERED:
			return {
				backgroundColor: SemanticColor.SUCCESS_100,
				borderColor: SemanticColor.SUCCESS,
				textColor: SemanticColor.SUCCESS_300,
			};
		case UserStatus.REGISTRATION_REJECTED:
			return {
				backgroundColor: SemanticColor.ERROR_100,
				borderColor: SemanticColor.ERROR_MEDIUM,
				textColor: SemanticColor.ERROR_300,
			};
	}
}

export function getUserRoleString(
	role: SystemRole,
	translation: TFunction<"translation", undefined>,
): string {
	switch (role) {
		case SystemRole.SYSTEM_ADMIN:
			return translation("SystemAdmin");
		case SystemRole.ADMIN:
			return translation("Admininstrator");
		case SystemRole.LAB_MANAGER:
			return translation("LabManager");
		case SystemRole.RECEPTIONIST:
			return translation("Receptionist");
		case SystemRole.SALES_REP:
			return translation("SalesRepresentative");

		case SystemRole.ANALYST:
			return translation("Analyst");
	}
}

export function getAnalystAuthorizationString(
	testCategory: FrontendCompanyTestCategory,
	user: FrontendUserInfo,
	translation: TFunction<"translation", undefined>,
): string {
	const result: Array<string> = [];
	user.analystRoleGroup?.permittedBatchIds.forEach((batchId) => {
		const matchingBatch: FrontendBatchType | undefined =
			testCategory.batchTypes.find((type) => {
				return type.batchTypeId == batchId;
			});
		if (matchingBatch) {
			result.push(matchingBatch.batchName);
		}
	});

	if (
		user.analystRoleGroup?.testCategoryOtherTestPermissions.includes(
			testCategory.testCategoryId,
		)
	) {
		result.push(translation("OtherTesting"));
	}

	return result.length > 0 ? result.join(", ") : translation("None");
}

export function getInitialContentPageByRole(
	role: SystemRole,
): FrontendContentPage {
	switch (role) {
		case SystemRole.SYSTEM_ADMIN:
		case SystemRole.ADMIN:
			return FrontendContentPage.INSTANCE_PAGE;
		case SystemRole.LAB_MANAGER:
			return FrontendContentPage.VIEW_ORDER_PAGE;

		case SystemRole.RECEPTIONIST:
			return FrontendContentPage.INSTANCE_PAGE;

		case SystemRole.SALES_REP:
			return FrontendContentPage.CLIENTS_PAGE;

		case SystemRole.ANALYST:
			return FrontendContentPage.INSTANCE_PAGE;
	}
}

export function getContentPageSuffix(page: FrontendContentPage): string {
	switch (page) {
		case FrontendContentPage.NEW_ORDER_PAGE:
			return "/portal/new-order";
		case FrontendContentPage.VIEW_ORDER_PAGE:
			return "/portal/view-order";
		case FrontendContentPage.USER_INFO_PAGE:
			return "/portal/user-info";
		case FrontendContentPage.ROSTER_PAGE:
			return "/portal/roster";
		case FrontendContentPage.CLIENTS_PAGE:
			return "/portal/clients";
		case FrontendContentPage.INSTANCE_PAGE:
			return "/portal/instances";

		case FrontendContentPage.BATCH_PAGE:
			return "/portal/batches";
		case FrontendContentPage.BATCH_GENERATION_PAGE:
			return "/portal/batch-generation";
		case FrontendContentPage.LOG_PAGE:
			return "/portal/logs";
		case FrontendContentPage.FILE_PASSWORD:
			return "/portal/file-password";
		case FrontendContentPage.LAB_STRUCTURE:
			return "/portal/lab-structure";
		default:
			return "/portal/instances";
	}
}
